import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import OrganizationHero from "./OrganizationHero";
import { Button } from "../ui/button";
import { FadeInUp } from "../animations/AnimationComponents";
import { GradientOrb } from "../animations/BackgroundEffects";
import useRenderLogger from "../../hooks/useRenderLogger";
import { getBusinessPath } from "../../routes/businessRoutes";
import { useI18n } from "../../hooks/useI18n";
import type {
  OrganizationPageData as BusinessPageData,
  OrganizationPageProduct as BusinessPageProduct,
} from "../../presentation/organizations/organizationPage.types";
import ProductCard from "./ProductCard";
import ProductModal from "./ProductModal";

interface VirtualizedGridProps {
  products: BusinessPageProduct[];
  renderProduct: (product: BusinessPageProduct) => ReactNode;
}

interface VirtualListProps {
  height: number;
  itemCount: number;
  itemSize: number;
  width: number;
  children: (props: { index: number; style: React.CSSProperties }) => ReactNode;
}

type VirtualListComponent = React.ComponentType<VirtualListProps>;

const VirtualizedGrid: React.FC<VirtualizedGridProps> = ({ products, renderProduct }) => {
  useRenderLogger("VirtualizedGrid", { count: products?.length });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(typeof window !== "undefined" ? window.innerWidth : 1024);
  const listRef = useRef<VirtualListComponent | null>(null);
  const [hasVirtual, setHasVirtual] = useState<boolean>(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      const onResize = () => setWidth(window.innerWidth);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    let resizeObserver: ResizeObserver | null = null;
    try {
      resizeObserver = new ResizeObserver((entries) => {
        if (!entries[0]) return;
        setWidth(entries[0].contentRect.width);
      });
      resizeObserver.observe(element);
    } catch {
      const onResize = () => setWidth(element.offsetWidth || window.innerWidth);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = await import("react-window");
        if (!mounted) return;
        const candidate = mod as unknown as {
          FixedSizeList?: VirtualListComponent;
          FixedSizeGrid?: VirtualListComponent;
          default?: VirtualListComponent | { FixedSizeList?: VirtualListComponent };
        };
        const defaultList = typeof candidate.default === "function"
          ? candidate.default
          : candidate.default?.FixedSizeList;
        listRef.current = candidate.FixedSizeList ?? defaultList ?? candidate.FixedSizeGrid ?? null;
        setHasVirtual(Boolean(listRef.current));
      } catch {
        setHasVirtual(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const gap = 32;
  const columns = useMemo(() => {
    if (width < 640) return 1;
    if (width < 1024) return 2;
    if (width < 1280) return 3;
    return 4;
  }, [width]);

  const colWidth = Math.floor((width - gap * (columns - 1)) / columns);
  const rowCount = Math.ceil(products.length / columns);
  const rowHeight = width < 640 ? 540 : 500;

  if (hasVirtual && listRef.current) {
    const ListComp = listRef.current;
    return (
      <div ref={containerRef} className="w-full">
        <ListComp
          height={Math.min(rowCount * rowHeight, Math.max(600, window.innerHeight - 200))}
          itemCount={rowCount}
          itemSize={rowHeight}
          width={width}
        >
          {({ index, style }: { index: number; style: React.CSSProperties }) => {
            const items: React.ReactNode[] = [];
            for (let column = 0; column < columns; column++) {
              const itemIndex = index * columns + column;
              const product = products[itemIndex];
              items.push(
                <div key={column} style={{ width: colWidth }}>
                  {product ? renderProduct(product) : <div style={{ height: rowHeight - 24 }} />}
                </div>
              );
            }

            return (
              <div style={{ ...style, display: "flex", gap: `${gap}px`, paddingRight: 4 }}>
                {items}
              </div>
            );
          }}
        </ListComp>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {products.map((product) => (
        <div key={product.id}>{renderProduct(product)}</div>
      ))}
    </div>
  );
};

interface ProductsSectionProps {
  business: BusinessPageData;
  LinkComponent: React.ComponentType<{ to: string; children: React.ReactNode }>;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({ business, LinkComponent }) => {
  const { text, t } = useI18n();
  type ProductType = BusinessPageData["products"][number];
  const [modalProduct, setModalProduct] = useState<null | ProductType>(null);
  const businessFallbackImage = business.logo;
  const renderProduct = useCallback((product: ProductType) => (
    <ProductCard
      product={product}
      fallbackImage={businessFallbackImage}
      previewPrice={product.presentations?.length ? t(text.business.from, { price: product.presentations[0].price }) : product.price}
      viewLabel={text.business.viewProduct}
      actionLabel={product.presentations && product.presentations.length >= 2
        ? text.business.choosePresentation
        : text.business.consult}
      onAction={setModalProduct}
    />
  ), [businessFallbackImage, t, text.business]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <OrganizationHero
        name={t(text.business.productsHero, { name: business.name })}
        description={text.business.productsSubtitle}
        heroImage={business.backgroundImage || businessFallbackImage}
        height="medium"
      />

      <section className="relative overflow-hidden bg-gray-50 py-16">
        <GradientOrb className="absolute right-10 top-10 -z-10" size="lg" color="gray" />
        <GradientOrb className="absolute bottom-20 left-10 -z-10" size="md" color="gray" />
        <div className="container mx-auto px-4">
          <FadeInUp delay={0.1}>
            <div className="mb-12 text-center">
              <h1 className="mb-3 text-3xl font-bold text-gray-800">{text.business.productCatalog}</h1>
              <div className="mx-auto mb-6 h-1 w-20 bg-primary"></div>
              <p className="mx-auto max-w-2xl text-lg text-gray-600">{text.business.productCatalogBody}</p>
            </div>
          </FadeInUp>

          {business.products.length > 0 ? (
            <>
              <FadeInUp>
                <VirtualizedGrid products={business.products} renderProduct={renderProduct} />
              </FadeInUp>
              <ProductModal
                product={modalProduct}
                organizationName={business.name}
                whatsapp={business.socialMedia.whatsapp}
                fallbackImage={businessFallbackImage}
                onClose={() => setModalProduct(null)}
                labels={{
                  weight: text.business.chooseVariantLabelWeight,
                  flavor: text.business.chooseVariantLabelFlavor,
                  presentation: text.business.presentation,
                  action: text.business.contactWhatsapp,
                }}
              />
            </>
          ) : (
            <FadeInUp delay={0.3}>
              <div className="rounded-lg border border-gray-200 bg-white py-12 text-center shadow-lg">
                <h3 className="mb-2 text-xl font-semibold text-gray-700">{text.business.noProducts}</h3>
                <p className="text-gray-500">{text.business.productsSoon}</p>
              </div>
            </FadeInUp>
          )}

          <FadeInUp delay={0.5}>
            <div className="mt-12 flex justify-center">
              <Button asChild variant="outline" className="border-primary text-primary transition-transform hover:scale-105">
                <LinkComponent to={getBusinessPath(business.id)}>{text.business.backHome}</LinkComponent>
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
};

export default ProductsSection;
